import type { SmartRoutingEntityType } from '@trinity/chat';

const entityLabelMap: Record<SmartRoutingEntityType, { singularWithArticle: string; plural: string }> = {
  account: {
    singularWithArticle: 'an Account',
    plural: 'Accounts',
  },
  hcp: {
    singularWithArticle: 'an HCP',
    plural: 'HCPs',
  },
};

function uniqueEntityTypes(entityTypes: SmartRoutingEntityType[]): SmartRoutingEntityType[] {
  return entityTypes.filter((entityType, index) => entityTypes.indexOf(entityType) === index);
}

function joinEntityLabels(labels: string[], conjunction: 'and' | 'or'): string {
  if (labels.length === 0) {
    return '';
  }

  if (labels.length === 1) {
    return labels[0] ?? '';
  }

  const allExceptLast = labels.slice(0, -1).join(', ');
  const last = labels[labels.length - 1] ?? '';

  return `${allExceptLast} ${conjunction} ${last}`;
}

export function getSmartRoutingEntityPluralLabel(entityTypes: SmartRoutingEntityType[]): string {
  const uniqueTypes = uniqueEntityTypes(entityTypes);
  const labels = uniqueTypes.map((entityType) => entityLabelMap[entityType].plural);
  return joinEntityLabels(labels, 'and');
}

/**
 * Collective name for a set of entity types: a single entity type names itself,
 * anything broader is described generically as Customers.
 */
export function getSmartRoutingEntityCollectiveLabel(entityTypes: SmartRoutingEntityType[]): string {
  const uniqueTypes = uniqueEntityTypes(entityTypes);

  if (uniqueTypes.length === 1 && uniqueTypes[0]) {
    return entityLabelMap[uniqueTypes[0]].plural;
  }

  return 'Customers';
}

export function getSmartRoutingEntitySearchPlaceholder(entityTypes: SmartRoutingEntityType[]): string {
  return `Select ${getSmartRoutingEntityCollectiveLabel(entityTypes)}`;
}

export function getSmartRoutingEntitySingularLabelWithArticle(
  entityTypes: SmartRoutingEntityType[]
): string {
  const uniqueTypes = uniqueEntityTypes(entityTypes);
  const labels = uniqueTypes.map((entityType) => entityLabelMap[entityType].singularWithArticle);
  return joinEntityLabels(labels, 'or');
}
